import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { cloneElement, isValidElement, memo, useCallback, useMemo, useState, useEffect, } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Drawer from '@mui/material/Drawer';
import { alpha } from '@mui/material/styles';
import { useLocation, useNavigate } from 'react-router-dom';
import { ComingSoonPage } from '@eruvaka/web-pondlogs-base';
import { HelpSupportView } from '@eruvaka/web-pondlogs-help-support';
import { LanguageSelector } from '@eruvaka/web-pondlogs-i18n';
import { PondListView } from '@eruvaka/web-pondlogs-ponds';
import { TopBar } from './TopBar';
import { ProfileMenu } from './ProfileMenu';
import { NotificationBell } from './NotificationBell';
import { Dashboard } from './Dashboard';
import { NavigationPanel } from './NavigationPanel';
import { SettingsView } from './SettingsView';
import { FarmSwitcher } from './FarmSwitcher';
import { endpoint, env } from '@eruvaka/web-pondlogs-base';
import { fetchUserProfile } from '../api/userApi';
export const InSessionView = memo((props) => {
    const { displayName, roleLabel, onSignOut, user, notifications = [], onMarkAllAsRead, pondsEmptyMapContent, showSetupLanding = false, } = props;
    const normalizeAvatarUrl = (value) => {
        if (!value)
            return undefined;
        const normalized = value.trim();
        if (!normalized)
            return undefined;
        const lower = normalized.toLowerCase();
        if (lower === 'default.png' ||
            lower.endsWith('/default.png') ||
            lower.includes('/settings/default.png')) {
            return undefined;
        }
        if (lower.startsWith('http://') ||
            lower.startsWith('https://') ||
            lower.startsWith('data:') ||
            lower.startsWith('blob:')) {
            return normalized;
        }
        return endpoint(env.apiBaseUrl, normalized);
    };
    const [avatarUrl, setAvatarUrl] = useState(normalizeAvatarUrl(user?.profilePhotoUrl ?? undefined));
    useEffect(() => {
        fetchUserProfile()
            .then((profile) => {
            if (profile.profilePhotoUrl) {
                setAvatarUrl(normalizeAvatarUrl(profile.profilePhotoUrl));
            }
        })
            .catch(() => {
            // silently ignore — avatar stays as default
        });
    }, []);
    const location = useLocation();
    const navigate = useNavigate();
    const pathname = location.pathname.toLowerCase();
    const activeMainView = pathname.startsWith('/ponds')
        ? 'ponds'
        : pathname === '/help'
            ? 'help'
            : pathname.startsWith('/settings')
                ? 'settings'
                : pathname === '/coming-soon'
                    ? 'coming-soon'
                    : 'dashboard';
    const [isDesktopNavOpen, setIsDesktopNavOpen] = useState(true);
    const [isMobileNavOpen, setIsMobileNavOpen] = useState(false);
    // Tracks whether the user clicked "Start Setup" — shows the map instead of the landing on /ponds
    const [pondsSetupStarted, setPondsSetupStarted] = useState(false);
    // Single handler: sets map-open flag and navigates to /ponds if not already there
    const openPondsMap = useCallback(() => {
        setPondsSetupStarted(true);
        if (activeMainView !== 'ponds') {
            navigate('/ponds');
        }
    }, [activeMainView, navigate]);
    const setupLandingContent = useMemo(() => {
        if (!pondsEmptyMapContent)
            return null;
        if (!isValidElement(pondsEmptyMapContent))
            return pondsEmptyMapContent;
        return cloneElement(pondsEmptyMapContent, {
            onStartSetup: openPondsMap,
        });
    }, [pondsEmptyMapContent, openPondsMap]);
    const onNavItemClick = (key) => {
        if (key === 'dashboard') {
            navigate('/landing');
            return;
        }
        if (key === 'help') {
            navigate('/help');
            return;
        }
        if (key === 'ponds') {
            navigate('/ponds');
            return;
        }
        if (key === 'settings') {
            navigate('/settings');
            return;
        }
        // Monitor, operations, analytics - coming soon features
        if (key === 'monitor' || key === 'operations' || key === 'analytics') {
            navigate('/coming-soon');
            return;
        }
    };
    const renderMainContent = () => {
        // Show the full-screen landing on all pages (including /ponds) when no locations,
        // UNLESS the user has already clicked "Start Setup" while on /ponds (then show the map).
        if (showSetupLanding &&
            activeMainView !== 'help' &&
            !(activeMainView === 'ponds' && pondsSetupStarted) &&
            setupLandingContent) {
            return setupLandingContent;
        }
        if (activeMainView === 'help')
            return _jsx(HelpSupportView, {});
        if (activeMainView === 'coming-soon')
            return _jsx(ComingSoonPage, {});
        if (activeMainView === 'settings') {
            const VALID_SECTIONS = ['preferences', 'profile-users'];
            const urlSection = location.pathname.split('/settings/')[1]?.split('/')[0];
            const initialSection = VALID_SECTIONS.includes(urlSection)
                ? urlSection
                : 'preferences';
            return (_jsx(SettingsView, { user: user ?? null, initialSection: initialSection, onProfilePhotoUpdated: (nextAvatarUrl) => setAvatarUrl(normalizeAvatarUrl(nextAvatarUrl)) }));
        }
        if (activeMainView === 'ponds') {
            return _jsx(PondListView, { emptyMapContent: pondsEmptyMapContent });
        }
        return _jsx(Dashboard, {});
    };
    return (_jsxs(Box, { sx: { width: '100%', minHeight: '100vh', bgcolor: 'background.default' }, children: [_jsxs(Box, { sx: { display: 'flex', width: '100%', minHeight: '100vh' }, children: [_jsx(Box, { sx: {
                            width: { md: isDesktopNavOpen ? 200 : 60 },
                            bgcolor: 'primary.main',
                            display: { xs: 'none', md: 'block' },
                            overflow: 'visible',
                            flexShrink: 0,
                            position: 'relative',
                            zIndex: 1001,
                            borderRight: (theme) => `1px solid ${alpha(theme.palette.common.white, 0.08)}`,
                            transition: 'width 280ms cubic-bezier(0.2, 0, 0, 1)',
                            willChange: 'width',
                        }, children: _jsx(NavigationPanel, { activeMainView: activeMainView, onNavItemClick: onNavItemClick, onClose: () => setIsDesktopNavOpen((prev) => !prev), collapsed: !isDesktopNavOpen }) }), _jsxs(Box, { sx: { flex: 1, minWidth: 0 }, children: [_jsxs(TopBar, { children: [_jsx(TopBar.Left, { children: _jsx(Box, { sx: { display: 'flex', alignItems: 'center', gap: 1 }, children: _jsx(Button, { variant: "text", size: "small", onClick: () => setIsMobileNavOpen(true), sx: {
                                                    color: 'primary.main',
                                                    p: 0.5,
                                                    display: { xs: 'inline-flex', md: 'none' },
                                                    minWidth: 'auto',
                                                }, children: "\u2630" }) }) }), _jsx(TopBar.Center, {}), _jsx(TopBar.Right, { children: _jsxs(Box, { sx: { display: 'flex', alignItems: 'center', gap: 2 }, children: [_jsx(FarmSwitcher, {}), _jsx(LanguageSelector, {}), _jsx(NotificationBell, { notifications: notifications, onMarkAllAsRead: onMarkAllAsRead }), _jsx(ProfileMenu, { displayName: displayName, roleLabel: roleLabel, onSignOut: onSignOut, avatarUrl: avatarUrl })] }) })] }), _jsx(Box, { sx: {
                                    p: activeMainView === 'settings' || activeMainView === 'help' ? 0 : { xs: 1, md: 1.5 },
                                    width: '100%',
                                    overflowY: 'auto',
                                    height: 'calc(100vh - 64px)',
                                }, children: renderMainContent() })] })] }), _jsx(Drawer, { open: isMobileNavOpen, onClose: () => setIsMobileNavOpen(false), anchor: "left", sx: { display: { xs: 'block', md: 'none' } }, PaperProps: {
                    sx: {
                        bgcolor: 'primary.main',
                        color: 'common.white',
                        borderRadius: '0 12px 12px 0',
                        width: 200,
                    },
                }, children: _jsx(NavigationPanel, { activeMainView: activeMainView, onNavItemClick: onNavItemClick, onClose: () => setIsMobileNavOpen(false) }) })] }));
});
